import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { BLOG_POSTS } from "@/data/blog";
import { formatDate } from "@/lib/api";
import { useSeo, breadcrumb, absolute, AUTEUR } from "@/lib/seo";
import { api } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";

const Blog = () => {
  const { locale, t } = useLocale();
  const [articles, setArticles] = useState(BLOG_POSTS);
  useEffect(() => {
    if (locale !== "en") { setArticles(BLOG_POSTS); return; }
    api.articleTranslations(BLOG_POSTS.map((article) => article.slug)).then((translations) => setArticles(BLOG_POSTS.map((article) => ({ ...article, ...(translations[article.slug] || {}) })))).catch(() => setArticles(BLOG_POSTS));
  }, [locale]);
  useSeo({
    title: locale === "en" ? "Journal — Drums, skins, lacing & symbolism · Entre Les Mondes" : "Blog — Tambours, peaux, laçages & symbolique · Entre les Mondes",
    description: locale === "en" ? "Articles by Ludovic Isoz: what drumming does to the brain, how to choose a drum skin, animal symbolism, lacing and tribal drumming." : "Articles de Ludovic Isoz : ce que le tambour fait au cerveau, comment choisir la peau de son tambour, la symbolique des animaux, les laçages, le tambour tribal.",
    image: articles[0].cover,
    jsonLd: [breadcrumb([[locale === "en" ? "Home" : "Accueil", "/"], [locale === "en" ? "Journal" : "Blog", "/blog"]]), { "@type": "Blog", name: "Blog Entre Les Mondes", url: absolute("/blog"), author: AUTEUR, blogPost: articles.map((p) => ({ "@type": "BlogPosting", headline: p.titre, description: p.accroche, datePublished: p.date, image: p.cover, url: absolute(`/blog/${p.slug}`), author: AUTEUR })) }],
  });
  const [premier, ...autres] = articles;

  return (
    <div>
      <section className="bg-earth grain text-sand relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-44 pb-20 relative z-[2]">
          <Reveal>
            <p className="eyebrow mb-6 text-sand/70">{locale === "en" ? "Journal" : "Le blog"}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">
              {locale === "en" ? "Workshop notes: skins, lacing and resonance." : "Carnets de l'atelier : peaux, laçages, résonances."}
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <Reveal>
          <Link to={`/blog/${premier.slug}`} data-testid={`blog-card-${premier.slug}`} className="group grid lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-24">
            <div className="lg:col-span-7 rounded-2xl overflow-hidden aspect-[16/10]">
              <img src={premier.cover} alt={premier.titre} className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
            </div>
            <div className="lg:col-span-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra mb-4">{premier.categorie} · {locale === "en" ? "Featured" : "À la une"}</p>
              <h2 className="font-serif text-3xl lg:text-4xl text-bark leading-tight group-hover:text-terra transition-colors duration-300">{premier.titre}</h2>
              <p className="text-fumee mt-4 leading-relaxed">{premier.accroche}</p>
              <p className="font-mono text-xs text-fumee mt-6 flex items-center gap-3 capitalize">{formatDate(premier.date, { weekday: undefined })} <span>·</span> <Clock className="w-3.5 h-3.5" /> {premier.lecture}</p>
              <span className="inline-flex items-center gap-2 mt-6 text-sm text-bark link-line">{locale === "en" ? "Read the article" : "Lire l'article"} <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" /></span>
            </div>
          </Link>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-14">
          {autres.map((p, i) => (
            <motion.div key={p.slug} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, delay: (i % 2) * 0.1 }}>
              <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block">
                <div className="rounded-2xl overflow-hidden aspect-[16/10] mb-6">
                  <img src={p.cover} alt={p.titre} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra mb-3">{p.categorie}</p>
                <h3 className="font-serif text-2xl text-bark leading-snug group-hover:text-terra transition-colors duration-300">{p.titre}</h3>
                <p className="text-sm text-fumee mt-3 leading-relaxed">{p.accroche}</p>
                <p className="font-mono text-xs text-fumee mt-4 flex items-center gap-3 capitalize">{formatDate(p.date, { weekday: undefined })} <span>·</span> <Clock className="w-3.5 h-3.5" /> {p.lecture}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Blog;
